import type { CustomStickersBundleProps } from '@athena-lib/CustomStickersBundle';
import type { EstimatedDeliveryProps } from '@athena-lib/EstimatedDelivery';
import type { OrderTotalProps } from '@athena-lib/OrderTotal/OrderTotal.svelte';
import type {
  CartShipping,
  CheckoutPageShipping,
  CheckoutStateData,
  createCheckoutInstance,
  Order,
} from '@stickerapp-org/checkout-sdk';

type checkoutInstance = ReturnType<typeof createCheckoutInstance>;
export type CheckoutSdk = {
  state: CheckoutStateData;
} & Omit<checkoutInstance, 'state'>;
export type CheckoutState = CheckoutSdk['state'];
export type CheckoutActions = CheckoutSdk['actions'];

type DiscountCode = {
  code: string;
  amount: {
    major: number;
    minor: number;
  } | null;
};

type DiscountCodes = DiscountCode[];

export const createCurrencyFormatter = ({
  language,
  currency,
}: {
  language: string;
  currency: string;
}) => {
  const { format } = new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  });

  return (value: number) => format(value);
};

const createDateFormatters = ({ language }: { language: string }) => {
  return {
    weekday: new Intl.DateTimeFormat(language, {
      weekday: 'short',
    }).format,
    month: new Intl.DateTimeFormat(language, {
      month: 'short',
    }).format,
    day: new Intl.DateTimeFormat(language, {
      day: 'numeric',
    }).format,
  };
};

function fixMonthCasing(formatted: string, checkoutState: CheckoutState): string {
  const lowerCaseLanguages = [
    'SE',
    'DK',
    'NO',
    'FI',
    'ES',
    'FR',
    'IT',
    'PT',
    'DE',
    'PL',
    'CZ',
    'SK',
    'SI',
    'HR',
    'RS',
    'RO',
    'HU',
    'GR',
  ];
  const countryCode = checkoutState.countryCode.toUpperCase();

  return lowerCaseLanguages.includes(countryCode)
    ? formatted.charAt(0).toLowerCase() + formatted.slice(1)
    : formatted;
}

const formatDeliveryDate = (checkoutState: CheckoutState, date: number) => {
  const dateFormatters = createDateFormatters(checkoutState);

  const weekday = fixMonthCasing(dateFormatters.weekday(date * 1000), checkoutState);
  const day = dateFormatters.day(date * 1000);
  const month = fixMonthCasing(dateFormatters.month(date * 1000), checkoutState);

  return `${weekday} ${day} ${month}`;
};

const getBundleSavingsFromOrder = (order: Order) => {
  const bundleUnits = order.lines.filter((item) => {
    return item.price.bundleSavings;
  }).length;
  return bundleUnits;
};

export const formatEstimatedDeliveryDates = (
  checkoutState: CheckoutState,
  shippingMethod: CheckoutPageShipping['selectedShippingMethod'],
) => {
  if (!shippingMethod) {
    return undefined;
  }

  const { minDate, maxDate } = shippingMethod.deliveryEstimate;

  const formattedMin = formatDeliveryDate(checkoutState, minDate);
  const formattedMax = formatDeliveryDate(checkoutState, maxDate);

  if (formattedMin === formattedMax) {
    return { formattedDeliveryDate: formattedMin, dateInterval: false };
  }

  return { formattedDeliveryDate: `${formattedMin} - ${formattedMax}`, dateInterval: true };
};

export const isCheckoutPageShipping = (shipping?: CartShipping): shipping is CheckoutPageShipping =>
  (shipping && shipping.$type === 'checkoutPage') ?? false;

export const getEstimatedDeliveryProps = (
  checkoutState: CheckoutState,
  getSuggestedPostalCode: () => { postalCode: string | undefined; country: string | undefined },
  countries: Record<string, string>[] = [],
): { estimation: EstimatedDeliveryProps['estimation'] } | undefined => {
  const { checkout, customer } = checkoutState;

  const postalCode = getSuggestedPostalCode();

  if (!isCheckoutPageShipping(checkout?.shipping) || !postalCode) {
    return undefined;
  }

  const estimatedDeliveryDates = formatEstimatedDeliveryDates(
    checkoutState,
    checkout.shipping.selectedShippingMethod,
  );

  if (!estimatedDeliveryDates) {
    return undefined;
  }

  const formattedPostalCode = [postalCode.postalCode, customer.address?.city, postalCode.country]
    .filter((x) => x)
    .join(', ');

  return {
    estimation: {
      ...estimatedDeliveryDates,
      formattedPostalCode,
      countryCode: postalCode.country?.toLowerCase() ?? '',
      formattedCountry:
        countries.find(
          (country: Record<string, string>) => country.code === postalCode.country?.toUpperCase(),
        )?.name ?? '',
    },
  };
};

const formatShippingPrice = (order: Order, formatCurrency: (value: number) => string) => {
  const priceKey = order.tax?.type === 'salesTax' ? 'amountExclTax' : 'amountInclTax';
  const checkoutShipping = order['shippingMethod'];
  return checkoutShipping.price.total.major[priceKey] === 0
    ? 'free'
    : formatCurrency(checkoutShipping.price.total.major[priceKey]);
};

export const getOrderTotalPropsFromOrder = (order: Order, language: string): OrderTotalProps => {
  const formatCurrency = createCurrencyFormatter({
    language,
    currency: order.currency,
  });

  const tax = order['tax'];

  const shippingText = formatShippingPrice(order, formatCurrency);
  const totalAmount = getTotalAmount(order);
  const taxPercentage = order?.tax?.isTaxExempt ? 0 : order?.total.taxPercentage;
  return {
    total: formatCurrency(totalAmount),
    subTotal:
      tax?.type === 'salesTax'
        ? formatCurrency(order.subTotal.major.amountExclTax)
        : formatCurrency(order.subTotal.major.amountInclTax),
    numOfItems: order.lines.length,
    shipping: shippingText,
    taxes: tax
      ? {
          type: tax.type,
          formattedValue: `${formatCurrency(tax.amountTotal.major ?? 0)}`,
          taxPercentage: `${taxPercentage}%`,
        }
      : undefined,
    discountCodes: formatDiscountCodes(formatCurrency, order.discountCodes),
    giftCards: order.usedGiftCards.map((x) => {
      return {
        amountFormatted: formatCurrency(-1 * Math.abs(x.amount.major)),
        code: x.code,
      };
    }),
    bundleSavings:
      order.bundleSavings && order.bundleSavings.major > 0
        ? {
            formattedDiscount: formatCurrency(order.bundleSavings.major),
            units: getBundleSavingsFromOrder(order),
          }
        : undefined,
    removeGiftCard: undefined,
    removeDiscountCode: undefined,
    isExport: false,
  };
};

export const getOrderTotalProps = (
  checkoutState: CheckoutState,
  removeCode: CheckoutActions['removeCode'],
  bundleSavings?: CustomStickersBundleProps,
): OrderTotalProps => {
  const formatCurrency = createCurrencyFormatter(checkoutState);
  const checkoutDetails = checkoutState.checkout;
  if (!checkoutDetails) {
    throw new Error('checkout details not loaded');
  }

  const salesTax = checkoutState.tax?.type === 'salesTax';
  const priceKey = salesTax ? 'amountExclTax' : 'amountInclTax';

  const checkoutShipping =
    checkoutDetails.shipping.$type === 'checkoutPage'
      ? checkoutDetails.shipping.selectedShippingMethod?.price.total.major[priceKey]
      : undefined;
  const taxPercentage = checkoutState.tax?.isTaxExempt
    ? 0
    : checkoutState.checkout?.total.taxPercentage;

  const shippingText =
    checkoutShipping === 0
      ? 'free'
      : checkoutShipping !== undefined
        ? formatCurrency(checkoutShipping)
        : undefined;

  return {
    total: formatCurrency(checkoutDetails.leftToPay.major.amountInclTax),
    subTotal: formatCurrency(checkoutDetails.subTotal.major[priceKey]),
    numOfItems: checkoutState.lines.length,
    shipping: shippingText,
    taxes: checkoutState.tax
      ? {
          type: checkoutState.tax.type,
          taxPercentage: `${taxPercentage}%`,
          formattedValue: `${formatCurrency(checkoutState.tax.amountTotal.major ?? 0)}`,
        }
      : undefined,
    discountCodes: formatDiscountCodes(formatCurrency, checkoutState.discountCodes),
    discountAmount: checkoutState.discountAmount
      ? formatCurrency(-1 * Math.abs(checkoutState.discountAmount))
      : undefined,
    giftCards: checkoutState.giftCards.map((x) => {
      return {
        amountFormatted: formatCurrency(-1 * Math.abs(x.amount?.major ?? 0)),
        remainingFormatted: x.amountRemaining?.major
          ? formatCurrency(x.amountRemaining.major)
          : undefined,
        code: x.code,
      };
    }),
    bundleSavings,
    removeDiscountCode: removeCode,
    removeGiftCard: removeCode,
    isExport: checkoutState.isExport,
  };
};

export const getIsManualOrder = (checkoutState: CheckoutState | null): boolean => {
  return !!checkoutState && typeof checkoutState.orderId === 'string';
};

export const getPaymentMethodImages = (state: CheckoutState): string[] => {
  const { checkout } = state;

  if (!checkout?.payment || checkout.payment.$type !== 'checkoutPage') {
    return [];
  }

  const logos = checkout.payment.paymentMethods.flatMap(({ id, logoUrls }) =>
    id === 'klarna' || !id.includes('klarna') ? logoUrls : [],
  );

  return Array.from(new Set(logos));
};

const formatDiscountCodes = (
  formatCurrency: (value: number) => string,
  discountCodes?: DiscountCodes,
) => {
  return discountCodes?.map(({ code, amount }) => ({
    code,
    formattedPrice: formatCurrency(-1 * Math.abs(amount?.major ?? 0)),
  }));
};

const getTotalAmount = (order: Order) => {
  if (typeof order.totalPaidInclTax === 'object') {
    return order.totalPaidInclTax.major;
  }
  return order.totalPaidInclTax;
};

// This type isn't exported?
export type CompletePurchaseClientResponse = Awaited<
  ReturnType<CheckoutActions['completePurchase']>
>;

type FedexTranslationKey =
  | 'fedexIpFast'
  | 'fedexIcpNormal'
  | 'fedexPoFast'
  | 'fedexPoExpress'
  | 'fedexIpExpress'
  | 'fedexIcpNormal2'
  | 'fedexIpNormal2'
  | 'fedex2dNormal2'
  | 'fedex2dExpress';

const translationMap: Record<string, Record<string, FedexTranslationKey>> = {
  fedexip: {
    fast: 'fedexIpFast',
    express: 'fedexIpExpress',
    normal2: 'fedexIpNormal2',
  },
  fedexicp: {
    normal: 'fedexIcpNormal',
    normal2: 'fedexIcpNormal2',
  },
  fedexpo: {
    fast: 'fedexPoFast',
    express: 'fedexPoExpress',
  },
  fedex2d: {
    normal2: 'fedex2dNormal2',
    express: 'fedex2dExpress',
  },
};

export const getTranslationKeyForFedex = (
  name: string,
  type: string,
): FedexTranslationKey | null => {
  return translationMap[name]?.[type] || null;
};
